import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../lib/supabase';
import { formatRating, averageRating, RATING_UNKNOWN } from '../../lib/ratings';

function timeAgo(ts) {
  if (!ts) return '—';
  const diff = Date.now() - new Date(ts).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d}d ago`;
  return new Date(ts).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function StatCard({ label, value, accent, sub, loading }) {
  return (
    <div style={{ background: 'var(--brand-white)', border: '1px solid var(--brand-sand)', borderTop: `3px solid ${accent}`, borderRadius: 'var(--radius-lg)', padding: '14px 16px' }}>
      <div style={{ fontSize: 'var(--font-size-xxs)', fontWeight: 700, color: 'var(--neutral-400)', textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 'var(--space-sm)' }}>{label}</div>
      <div style={{ fontFamily: 'var(--font-display)', fontSize: 26, fontWeight: 700, color: 'var(--neutral-800)', letterSpacing: -1, lineHeight: 1 }}>
        {loading ? '—' : (typeof value === 'number' ? value.toLocaleString() : value)}
      </div>
      {sub && <div style={{ fontSize: 'var(--font-size-xs)', fontWeight: 600, marginTop: 5, color: 'var(--neutral-500)' }}>{sub}</div>}
    </div>
  );
}

function Avatar({ username, size = 32 }) {
  const palettes = [
    { bg: '#fef3c7', color: 'var(--feedback-warning-ink)' }, { bg: '#d1fae5', color: '#065f46' },
    { bg: '#dbeafe', color: '#1e40af' }, { bg: '#f3e8ff', color: '#6b21a8' },
    { bg: '#fee2e2', color: '#991b1b' }, { bg: '#e0f2fe', color: '#0369a1' },
  ];
  const { bg, color } = palettes[(username || 'a').charCodeAt(0) % palettes.length];
  return (
    <div style={{ width: size, height: size, borderRadius: 'var(--radius-circle)', flexShrink: 0, background: bg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: Math.round(size * 0.35), fontWeight: 700, color }}>
      {(username || 'U').slice(0, 2).toUpperCase()}
    </div>
  );
}

function ImportRow({ imp, last, isMobile }) {
  const [expanded, setExpanded] = useState(false);

  if (isMobile) {
    return (
      <div style={{ borderBottom: last ? 'none' : '1px solid var(--brand-cream)' }}>
        <div style={{ padding: '14px 0' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-10)', marginBottom: 'var(--space-10)' }}>
            <Avatar username={imp.username} size={36} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 'var(--font-size-md)', fontWeight: 700, color: 'var(--neutral-800)' }}>@{imp.username}</div>
              <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--neutral-400)', fontFamily: 'var(--font-mono)' }}>
                goodreads_export.csv · {timeAgo(imp.updated_at)}
              </div>
            </div>
            <button onClick={() => setExpanded(p => !p)} style={{ fontSize: 11.5, fontWeight: 600, padding: '6px 12px', borderRadius: 'var(--radius-md)', border: '1px solid var(--brand-sand)', background: 'var(--brand-white)', color: 'var(--neutral-600)', cursor: 'pointer', fontFamily: 'var(--font-body)', flexShrink: 0, minHeight: 34 }}>
              {expanded ? 'Close ↑' : 'Details ↓'}
            </button>
          </div>
          <div style={{ display: 'flex', gap: 'var(--space-sm)', paddingLeft: 46 }}>
            <span style={{ fontSize: 'var(--font-size-sm)', fontWeight: 600, color: 'var(--neutral-800)', background: 'var(--brand-cream)', padding: '3px 10px', borderRadius: 'var(--radius-md)' }}>{imp.books_read} books</span>
            <span style={{ fontSize: 'var(--font-size-sm)', fontWeight: 600, color: '#2563eb', background: '#dbeafe', padding: '3px 10px', borderRadius: 'var(--radius-md)' }}>{imp.unique_authors} authors</span>
            {/* An import with no rated books stored a literal 0, and "★ 0.0"
                reads as "they hated everything". They rated nothing. */}
            <span style={{ fontSize: 'var(--font-size-sm)', fontWeight: 600, color: 'var(--feedback-warning)', background: 'var(--feedback-warning-bg)', padding: '3px 10px', borderRadius: 'var(--radius-md)' }}>
              {formatRating(imp.avg_rating, { treatZeroAsUnknown: true }) === RATING_UNKNOWN
                ? 'no ratings'
                : `★ ${formatRating(imp.avg_rating, { treatZeroAsUnknown: true })}`}
            </span>
          </div>
        </div>
        {expanded && (
          <div style={{ padding: '10px 0 14px 46px', background: 'var(--brand-paper)', borderTop: '1px solid var(--brand-cream)' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-sm)' }}>
              {imp.five_star_count > 0 && <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--neutral-600)' }}>⭐ <strong>{imp.five_star_count}</strong> five-star reads</div>}
              {imp.total_pages > 0 && <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--neutral-600)' }}>📖 <strong>{imp.total_pages.toLocaleString()}</strong> total pages</div>}
              {imp.top_tropes?.length > 0 && (
                <div>
                  <div style={{ fontSize: 'var(--font-size-xxs)', fontWeight: 700, color: 'var(--neutral-400)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 'var(--space-6)' }}>Top tropes</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                    {imp.top_tropes.slice(0, 5).map((t, i) => (
                      <span key={i} style={{ fontSize: 'var(--font-size-xs)', padding: '3px 9px', borderRadius: 'var(--radius-10)', background: 'var(--feedback-warning-bg)', border: '1px solid var(--feedback-warning-border)', color: 'var(--feedback-warning-ink)' }}>{t}</span>
                    ))}
                  </div>
                </div>
              )}
              {imp.top_genres?.length > 0 && (
                <div>
                  <div style={{ fontSize: 'var(--font-size-xxs)', fontWeight: 700, color: 'var(--neutral-400)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 'var(--space-6)' }}>Top genres</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                    {imp.top_genres.slice(0, 5).map((g, i) => (
                      <span key={i} style={{ fontSize: 'var(--font-size-xs)', padding: '3px 9px', borderRadius: 'var(--radius-10)', background: 'var(--brand-white)', border: '1px solid var(--brand-sand)', color: 'var(--neutral-600)' }}>{g}</span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }

  // Desktop
  return (
    <div style={{ borderBottom: last ? 'none' : '1px solid var(--brand-cream)' }}>
      <div style={{ display: 'grid', gridTemplateColumns: '36px 1fr 90px 90px 90px 110px', gap: '0 14px', padding: '12px 0', alignItems: 'center' }}>
        <Avatar username={imp.username} size={34} />
        <div>
          <div style={{ fontSize: 'var(--font-size-base)', fontWeight: 700, color: 'var(--neutral-800)', marginBottom: 'var(--space-xxs)' }}>@{imp.username}</div>
          <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--neutral-400)', fontFamily: 'DM Mono, monospace' }}>
            goodreads_export.csv · {timeAgo(imp.updated_at)}
          </div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: 'var(--font-size-md)', fontWeight: 700, color: 'var(--neutral-800)' }}>{imp.books_read}</div>
          <div style={{ fontSize: 'var(--font-size-xxs)', color: 'var(--neutral-400)' }}>books</div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: 'var(--font-size-md)', fontWeight: 700, color: '#2563eb' }}>{imp.unique_authors}</div>
          <div style={{ fontSize: 'var(--font-size-xxs)', color: 'var(--neutral-400)' }}>authors</div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: 'var(--font-size-md)', fontWeight: 700, color: 'var(--feedback-warning)' }}>
            {formatRating(imp.avg_rating, { treatZeroAsUnknown: true }) === RATING_UNKNOWN
              ? RATING_UNKNOWN
              : `★ ${formatRating(imp.avg_rating, { treatZeroAsUnknown: true })}`}
          </div>
          <div style={{ fontSize: 'var(--font-size-xxs)', color: 'var(--neutral-400)' }}>avg rating</div>
        </div>
        <div style={{ display: 'flex', gap: 'var(--space-6)', justifyContent: 'flex-end' }}>
          <button onClick={() => setExpanded(p => !p)} style={{ fontSize: 11.5, fontWeight: 600, padding: '5px 12px', borderRadius: 'var(--radius-md)', border: '1px solid var(--brand-sand)', background: 'var(--brand-white)', color: 'var(--neutral-600)', cursor: 'pointer', fontFamily: 'var(--font-body)' }}>
            {expanded ? 'Close ↑' : 'Details ↓'}
          </button>
        </div>
      </div>
      {expanded && (
        <div style={{ padding: '10px 0 14px 50px', background: 'var(--brand-paper)', borderTop: '1px solid var(--brand-cream)' }}>
          <div style={{ display: 'flex', gap: 'var(--space-h3)' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-6)' }}>
              {imp.five_star_count > 0 && <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--neutral-600)' }}>⭐ <strong>{imp.five_star_count}</strong> five-star reads</div>}
              {imp.total_pages > 0 && <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--neutral-600)' }}>📖 <strong>{imp.total_pages.toLocaleString()}</strong> total pages</div>}
            </div>
            {imp.top_tropes?.length > 0 && (
              <div>
                <div style={{ fontSize: 'var(--font-size-xxs)', fontWeight: 700, color: 'var(--neutral-400)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 'var(--space-6)' }}>Top tropes</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                  {imp.top_tropes.slice(0, 5).map((t, i) => (
                    <span key={i} style={{ fontSize: 'var(--font-size-xs)', padding: '3px 9px', borderRadius: 'var(--radius-10)', background: 'var(--feedback-warning-bg)', border: '1px solid var(--feedback-warning-border)', color: 'var(--feedback-warning-ink)' }}>{t}</span>
                  ))}
                </div>
              </div>
            )}
            {imp.top_genres?.length > 0 && (
              <div>
                <div style={{ fontSize: 'var(--font-size-xxs)', fontWeight: 700, color: 'var(--neutral-400)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 'var(--space-6)' }}>Top genres</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                  {imp.top_genres.slice(0, 5).map((g, i) => (
                    <span key={i} style={{ fontSize: 'var(--font-size-xs)', padding: '3px 9px', borderRadius: 'var(--radius-10)', background: 'var(--brand-white)', border: '1px solid var(--brand-sand)', color: 'var(--neutral-600)' }}>{g}</span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default function CsvSection({ isMobile }) {
  // Before the load finishes there is no average, so the placeholder is the em
  // dash rather than '0.0' — which is a number, and one nobody measured.
  const [stats,   setStats]   = useState({ total: 0, booksRead: 0, avgRating: RATING_UNKNOWN, fiveStars: 0 });
  const [imports, setImports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search,  setSearch]  = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // Use actual columns from user_csv_stats
      const { data: csvStats, error } = await supabase
        .from('user_csv_stats')
        .select('user_id, books_read, total_pages, avg_rating, unique_authors, five_star_count, top_tropes, top_genres, updated_at')
        .order('updated_at', { ascending: false })
        .limit(100);

      if (error) { console.error('CsvSection error:', error.message); setLoading(false); return; }

      const userIds = (csvStats || []).map(s => s.user_id).filter(Boolean);
      let profileMap = {};
      if (userIds.length) {
        const { data: profiles } = await supabase
          .from('user_profiles')
          .select('user_id, username, avatar_url')
          .in('user_id', userIds);
        (profiles || []).forEach(p => { profileMap[p.user_id] = p; });
      }

      const rows = (csvStats || []).map(stat => ({
        ...stat,
        username:    profileMap[stat.user_id]?.username || 'reader',
        avatar_url:  profileMap[stat.user_id]?.avatar_url || null,
        top_tropes:  Array.isArray(stat.top_tropes) ? stat.top_tropes : (stat.top_tropes ? Object.keys(stat.top_tropes) : []),
        top_genres:  Array.isArray(stat.top_genres) ? stat.top_genres : (stat.top_genres ? Object.keys(stat.top_genres) : []),
      }));

      const totalBooks  = rows.reduce((s, r) => s + (r.books_read || 0), 0);
      const totalFive   = rows.reduce((s, r) => s + (r.five_star_count || 0), 0);
      // Readers who rated nothing used to count as 0 in BOTH the total and the
      // divisor, so this number read lower than the community's actual average
      // by however many of them there are. They are now left out of both, which
      // is what the tooltip on the author page has always claimed this kind of
      // average does. Null when nobody has rated anything — not 0.0, which
      // would be an average of no data.
      const ratedAvg    = averageRating(rows.map(r => r.avg_rating), { treatZeroAsUnknown: true });
      const avgRating   = ratedAvg === null ? RATING_UNKNOWN : ratedAvg.toFixed(1);

      setImports(rows);
      setStats({ total: rows.length, booksRead: totalBooks, avgRating, fiveStars: totalFive });
    } catch (err) {
      console.error('CsvSection load error:', err.message);
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = imports.filter(i =>
    !search || (i.username || '').toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div style={{ padding: isMobile ? '20px 16px' : '30px 36px', fontFamily: 'var(--font-body)' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 'var(--space-md)', flexWrap: isMobile ? 'wrap' : 'nowrap', marginBottom: isMobile ? 12 : 20 }}>
        <div>
          <h1 style={{ fontFamily: 'var(--font-display)', fontSize: isMobile ? 22 : 26, fontWeight: 700, color: 'var(--neutral-800)', letterSpacing: -0.6, margin: 'var(--space-none)' }}>CSV Imports</h1>
          <p style={{ fontSize: 'var(--font-size-base)', color: 'var(--neutral-500)', marginTop: 'var(--space-xs)', marginBottom: 'var(--space-none)' }}>Goodreads library imports · drill in to see reading stats</p>
        </div>
        <input
          value={search} onChange={e => setSearch(e.target.value)}
          placeholder="Search by username..."
          style={{ width: isMobile ? '100%' : 240, padding: '9px 14px', fontSize: 'var(--font-size-lg)', border: '1.5px solid var(--brand-sand)', borderRadius: 'var(--radius-10)', outline: 'none', fontFamily: 'var(--font-body)', color: 'var(--neutral-800)', background: 'var(--brand-white)', boxSizing: 'border-box' }}
        />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)', gap: isMobile ? 10 : 14, marginBottom: 18 }}>
        <StatCard label="Readers imported"  value={stats.total}     accent="#292524" sub="total imports"    loading={loading} />
        <StatCard label="Total books read"  value={stats.booksRead} accent="#16a34a" sub="across all users" loading={loading} />
        <StatCard label="Avg rating"        value={stats.avgRating} accent="#d97706" sub="community avg"    loading={loading} />
        <StatCard label="Five-star reads"   value={stats.fiveStars} accent="#B85C38" sub="loved books"      loading={loading} />
      </div>

      <div style={{ background: 'var(--brand-white)', border: '1px solid var(--brand-sand)', borderRadius: 'var(--radius-lg)', padding: isMobile ? '16px' : '20px 22px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 'var(--space-14)' }}>
          <div style={{ fontSize: 'var(--font-size-base)', fontWeight: 700, color: 'var(--neutral-800)' }}>
            Recent imports
            {!isMobile && <span style={{ fontWeight: 400, color: 'var(--neutral-400)', fontSize: 'var(--font-size-sm)', marginLeft: 'var(--space-sm)' }}>click Details to drill in</span>}
          </div>
          <span style={{ fontSize: 11.5, color: 'var(--neutral-400)' }}>{filtered.length} import{filtered.length !== 1 ? 's' : ''}</span>
        </div>

        {!isMobile && (
          <div style={{ display: 'grid', gridTemplateColumns: '36px 1fr 90px 90px 90px 110px', gap: '0 14px', padding: '4px 0 10px', borderBottom: '1px solid var(--brand-sand)', fontSize: 'var(--font-size-xxs)', fontWeight: 700, color: 'var(--neutral-400)', textTransform: 'uppercase', letterSpacing: 0.5 }}>
            <div /><div>Reader</div>
            <div style={{ textAlign: 'right' }}>Books</div>
            <div style={{ textAlign: 'right' }}>Authors</div>
            <div style={{ textAlign: 'right' }}>Avg ★</div>
            <div />
          </div>
        )}

        {loading ? (
          <div style={{ padding: '40px 0', textAlign: 'center', color: 'var(--neutral-400)', fontSize: 'var(--font-size-base)' }}>Loading imports...</div>
        ) : !filtered.length ? (
          <div style={{ padding: '40px 0', textAlign: 'center', color: 'var(--neutral-400)', fontSize: 'var(--font-size-base)', fontStyle: 'italic' }}>
            {search ? 'No imports match your search' : 'No CSV imports yet'}
          </div>
        ) : (
          filtered.map((imp, idx) => (
            <ImportRow key={imp.user_id} imp={imp} last={idx === filtered.length - 1} isMobile={isMobile} />
          ))
        )}
      </div>

      <div style={{ marginTop: 'var(--space-14)', padding: '12px 16px', background: '#fffbeb', border: '1px solid var(--feedback-warning-border)', borderRadius: 'var(--radius-10)', fontSize: 12.5, color: 'var(--feedback-warning-ink)', lineHeight: 1.6 }}>
        <strong>How CSV imports work:</strong> Readers upload their Goodreads CSV export. Reading stats (books, authors, ratings, tropes) are stored per user and shown here.
      </div>
    </div>
  );
}